import { TFunction } from 'i18next';
import { createColumnHelper } from '@tanstack/react-table';
import {
  Badge,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
} from '@chakra-ui/react';
import { Icons } from '@texas/components/shared/Icons';
import { ArchivedTooltip } from '@texas/components/shared/tooltip/ArchivedTooltip';
import { Material } from '@texas/api/endpoints/metadata/materialsApi';

export const materialTableColumns = (
  t: TFunction,
  isMixed: boolean,
  onArchiveOpen: (material: Material) => void,
  onRestore: (id: number) => Promise<void>,
  onEditOpen: (id: number) => void,
) => {
  const columnHelper = createColumnHelper<Material>();
  const columns = [
    columnHelper.accessor('code', {
      cell: (info) => info.getValue(),
      header: t('general.code'),
      maxSize: 100,
    }),
    columnHelper.accessor('name', {
      cell: (info) => info.getValue(),
      header: t('general.name'),
      maxSize: 200,
    }),
    columnHelper.accessor('description', {
      cell: (info) => info.getValue(),
      header: t('general.description'),
      maxSize: 200,
    }),
    columnHelper.accessor('articleTextField', {
      cell: (info) => info.getValue(),
      header: t('configuration.articleTextField'),
      enableSorting: false,
    }),
    columnHelper.accessor('certificationTextField', {
      cell: (info) => info.getValue(),
      header: t('configuration.certificationTextField'),
      enableSorting: false,
    }),
    columnHelper.accessor('lockArticleAfterSync', {
      cell: (info) => (info.getValue() ? <Icons.Checkmark /> : ''),
      header: t('configuration.lockMtrlAfterSyncCol'),
      enableSorting: true,
    }),
    columnHelper.accessor('displayOrder', {
      cell: (info) => info.getValue(),
      header: t('branch.displayOrder'),
      enableSorting: true,
    }),
    ...(isMixed
      ? [
          columnHelper.accessor('combinationOf', {
            cell: (info) => (
              <Flex gap={2} flexWrap="wrap">
                {info.getValue().map((v) => (
                  <Badge key={v.id} variant="configuration-reference">
                    {v.name}
                  </Badge>
                ))}
              </Flex>
            ),
            header: t('configuration.combinationOf'),
            enableSorting: false,
          }),
        ]
      : []),
    columnHelper.accessor('id', {
      cell: (info) => (
        <>
          <Flex justifyContent="end" alignItems="center" gap={4}>
            {info.row.original.archived && (
              <ArchivedTooltip archived={info.row.original.archived} />
            )}
            <Menu placement="left" variant="entityDirectory">
              <MenuButton float="right" aria-label={t('general.more')}>
                <Icons.DotsHorizontal boxSize={6} />
              </MenuButton>
              <MenuList key={info.row.original.id}>
                <MenuItem
                  icon={<Icons.Pencil boxSize={5} />}
                  onClick={() => onEditOpen(info.row.original.id)}
                >
                  {t('general.edit')}
                </MenuItem>
                {info.row.original.archived ? (
                  <MenuItem
                    color="entityDirectory.primaryBackground"
                    icon={<Icons.Undo boxSize={5} />}
                    onClick={() => onRestore(info.row.original.id)}
                  >
                    {t('general.restore')}
                  </MenuItem>
                ) : (
                  <MenuItem
                    color="entityDirectory.dangerText"
                    icon={<Icons.Archive boxSize={5} />}
                    onClick={() => onArchiveOpen(info.row.original)}
                  >
                    {t('general.archive')}
                  </MenuItem>
                )}
              </MenuList>
            </Menu>
          </Flex>
        </>
      ),
      header: '',
      enableSorting: false,
      maxSize: 100,
    }),
  ];

  return columns;
};
